import { useCallback, useRef, useState } from "react";

import { useTwoFactorAction, type UseTwoFactorActionResult } from "@/shared/hooks/useTwoFactorAction";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import {
  type PayoutBatchDetail,
  PayoutBatchItemStatus,
  type RetryPreview,
} from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";

/** The items endpoint caps a page at 200; a batch has at most 1000 rows. */
const ID_PAGE_SIZE = 200;
const MAX_RETRY_IDS = 1000;

export interface UseRetryFailedItemsResult {
  preview: RetryPreview | null;
  isPreviewing: boolean;
  previewError: BatchError | null;
  /** Lists every FAILED row of the batch and prices it again, without persisting anything. */
  loadPreview: () => Promise<void>;
  /** Approves the previewed rows at the previewed total. Resolves true when the retry was accepted. */
  retry: () => Promise<boolean>;
  isRetrying: boolean;
  error: BatchError | null;
  /** The approved total was exceeded when the server re-priced; the preview was reloaded. */
  debitChanged: boolean;
  reset: () => void;
  twoFactor: UseTwoFactorActionResult<PayoutBatchDetail>;
}

async function collectFailedItemIds(batchId: string): Promise<string[]> {
  const ids: string[] = [];
  let skip = 0;
  for (;;) {
    const { data } = await PayoutBatchesService.listItems(batchId, {
      skip,
      limit: ID_PAGE_SIZE,
      status: PayoutBatchItemStatus.FAILED,
    });
    ids.push(...data.data.map((item) => item.id));
    skip += data.data.length;
    if (data.data.length < ID_PAGE_SIZE || skip >= data.total || ids.length >= MAX_RETRY_IDS) break;
  }
  return ids.slice(0, MAX_RETRY_IDS);
}

/**
 * The two-step retry: `POST /items/retry/preview` prices the failed rows again so the person approves a debit
 * they have seen, then `POST /items/retry` carries that total as `max_total_debit`. A 409
 * `retry_debit_exceeded` means the price moved between the two, and `retry_rows_changed` that another retry
 * took the rows first — either way the preview reloads and the person looks again.
 */
export function useRetryFailedItems(
  batchId: string,
  applyBatch: (next: PayoutBatchDetail) => void,
): UseRetryFailedItemsResult {
  const twoFactor = useTwoFactorAction<PayoutBatchDetail>();
  const [preview, setPreview] = useState<RetryPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<BatchError | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<BatchError | null>(null);
  const [debitChanged, setDebitChanged] = useState(false);
  const previewSeq = useRef(0);
  const { start } = twoFactor;

  const loadPreview = useCallback(async () => {
    const seq = ++previewSeq.current;
    setIsPreviewing(true);
    setPreviewError(null);
    try {
      const ids = await collectFailedItemIds(batchId);
      if (seq !== previewSeq.current) return;
      if (ids.length === 0) {
        setPreview({
          batch_id: batchId,
          source_currency: "",
          currency: "",
          items: [],
          retryable_count: 0,
          total_debit: "0",
        });
        return;
      }
      const { data } = await PayoutBatchesService.retryPreview(batchId, ids);
      if (seq !== previewSeq.current) return;
      setPreview(data);
    } catch (err: unknown) {
      if (seq !== previewSeq.current) return;
      setPreviewError(classifyBatchError(err));
    } finally {
      if (seq === previewSeq.current) setIsPreviewing(false);
    }
  }, [batchId]);

  const retry = useCallback(async (): Promise<boolean> => {
    if (!preview) return false;
    const itemIds = preview.items.filter((item) => item.pricing && !item.error).map((item) => item.id);
    if (itemIds.length === 0) return false;
    setError(null);
    setDebitChanged(false);
    try {
      const result = await start(async (code) => {
        setIsRetrying(true);
        const { data } = await PayoutBatchesService.retryItems(
          batchId,
          { itemIds, maxTotalDebit: preview.total_debit },
          code,
        );
        return data;
      });
      if (!result) return false;
      applyBatch(result);
      return true;
    } catch (err: unknown) {
      const classified = classifyBatchError(err);
      if (classified.code === "retry_debit_exceeded") {
        setDebitChanged(true);
        void loadPreview();
      } else {
        setError(classified);
        if (classified.code === "retry_rows_changed") void loadPreview();
      }
      return false;
    } finally {
      setIsRetrying(false);
    }
  }, [applyBatch, batchId, loadPreview, preview, start]);

  const reset = useCallback(() => {
    previewSeq.current += 1;
    setPreview(null);
    setPreviewError(null);
    setError(null);
    setDebitChanged(false);
    setIsPreviewing(false);
    setIsRetrying(false);
  }, []);

  return {
    preview,
    isPreviewing,
    previewError,
    loadPreview,
    retry,
    isRetrying,
    error,
    debitChanged,
    reset,
    twoFactor,
  };
}
